import { QueryClient } from '@tanstack/react-query';
import { ApiRequestError } from './api';
import { getDmSocket } from './ws';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiRequestError && err.status < 500) && count < 2,
    },
  },
});

/**
 * Perf (D-293): server invalidations, de-duplicated. A burst of mutations (tokens moving, a fog
 * stroke, a player spamming rolls) used to cancel and restart the same refetch once per message —
 * 48 token moves fired 800+ requests. Now a key whose queries are already fetching is held until that
 * fetch settles and then refetched ONCE (so the data still reflects the latest change); keys of the
 * same task are merged. A lone invalidation still refetches at once (no added latency).
 */
const heldKeys = new Map<string, { key: unknown[]; since: number }>();
let heldTimer: ReturnType<typeof setTimeout> | null = null;
/** A slow fetch never holds an invalidation longer than this. */
const MAX_HOLD_MS = 1500;

function invalidateKey(key: unknown[], since = performance.now()) {
  if (performance.now() - since < MAX_HOLD_MS && queryClient.isFetching({ queryKey: key }) > 0) {
    const id = JSON.stringify(key);
    if (!heldKeys.has(id)) heldKeys.set(id, { key, since });
    heldTimer ??= setTimeout(flushHeld, 60);
    return;
  }
  void queryClient.invalidateQueries({ queryKey: key });
}

function flushHeld() {
  heldTimer = null;
  const held = [...heldKeys.values()];
  heldKeys.clear();
  for (const h of held) invalidateKey(h.key, h.since);
}

/** Wire server `invalidate` messages to TanStack Query (prefix match on keys). */
export function bindInvalidation() {
  const sock = getDmSocket();
  const offInv = sock.onType('invalidate', (m) => {
    for (const key of m.keys) invalidateKey(key as unknown[]);
  });
  const offWorld = sock.onType('world.changed', () => {
    queryClient.invalidateQueries();
  });
  // Invalidations sent while the socket was down (server restart, Wi-Fi blip) are lost: refetch
  // everything once it is back.
  let wasOpen = sock.status === 'open';
  let dropped = false;
  const offStatus = sock.onStatus((s) => {
    if (s === 'open') {
      if (wasOpen && dropped) queryClient.invalidateQueries();
      wasOpen = true;
      dropped = false;
    } else if (wasOpen) dropped = true;
  });
  return () => {
    offInv();
    offWorld();
    offStatus();
  };
}
